import { parseTree, printParseErrorCode, type Node, type ParseError } from "jsonc-parser";
import type { JsonDocument, JsonLocation, JsonParse, JsonSpan } from "./public";

/**
 * 사람이 친 JSON 텍스트를 읽는다. `JSON.parse` 와 달리 오류 위치와 값마다의 텍스트 범위를 준다.
 *
 * 표준 JSON 만 받는다. 주석 · 끝 쉼표 · 빈 텍스트는 문법 오류다. 오류가 하나 생기면 뒤 오류가 잇달아
 * 생기므로 첫 문법 오류만 알린다. 같은 객체 안의 중복 키도 오류다. `JSON.parse` 는 나중 값을 조용히 쓴다.
 */
export function parseJsonDocument(text: string): JsonParse {
  const errors: ParseError[] = [];
  const root = parseTree(text, errors, {
    disallowComments: true,
    allowTrailingComma: false,
    allowEmptyContent: false,
  });
  const first = errors[0];
  if (first !== undefined) {
    return {
      ok: false,
      problem: {
        span: { offset: first.offset, length: first.length },
        text: describeSyntaxError(text, first),
      },
    };
  }
  if (root === undefined) {
    return {
      ok: false,
      problem: { span: { offset: 0, length: 0 }, text: "JSON 값이 없습니다." },
    };
  }
  const duplicate = findDuplicateKey(root);
  if (duplicate !== null) {
    return {
      ok: false,
      problem: {
        span: spanOf(duplicate),
        text: `"${String(duplicate.value)}" 키가 한 객체에 두 번 있습니다. 하나만 남겨 주세요.`,
      },
    };
  }
  return { ok: true, document: createDocument(root) };
}

/** 0부터 센 offset 을 1부터 센 줄 · 열로 바꾼다. 열은 UTF-16 코드 단위다(Monaco 규약). */
export function lineColumnAt(text: string, offset: number): { line: number; column: number } {
  const end = Math.max(0, Math.min(offset, text.length));
  let line = 1;
  let lineStart = 0;
  for (let index = 0; index < end; index += 1) {
    if (text.charCodeAt(index) === 10) {
      line += 1;
      lineStart = index + 1;
    }
  }
  return { line, column: end - lineStart + 1 };
}

function createDocument(root: Node): JsonDocument {
  return {
    value: toValue(root),
    locate: (path) => locate(root, path),
  };
}

/**
 * 점 경로가 가리키는 값을 찾는다. 경로 중간에서 멈추면 찾은 가장 깊은 값을 주고 `exact` 를 거짓으로 둔다.
 * 빠진 키의 문제는 그 부모를 가리키게 된다.
 */
function locate(root: Node, path: string): JsonLocation {
  let node = root;
  let exact = true;
  for (const segment of path === "" ? [] : path.split(".")) {
    const child = childAt(node, segment);
    if (child === null) {
      exact = false;
      break;
    }
    node = child;
  }
  const property = node.parent?.type === "property" ? node.parent : null;
  const key = property?.children?.[0];
  return {
    value: spanOf(node),
    key: key === undefined ? null : spanOf(key),
    container: node.type === "object" || node.type === "array",
    exact,
  };
}

function childAt(node: Node, segment: string): Node | null {
  if (node.type === "array") {
    const index = Number(segment);
    return Number.isInteger(index) ? (node.children?.[index] ?? null) : null;
  }
  if (node.type === "object") {
    const property = node.children?.find((child) => child.children?.[0]?.value === segment);
    return property?.children?.[1] ?? null;
  }
  return null;
}

/**
 * 트리를 값으로 바꾼다. 키는 `defineProperty` 로 둔다. 그래서 `"__proto__"` 키도 자기 property 가 되고
 * 스키마가 모르는 키로 잡힌다. 대입하면 prototype 을 바꾸고 키는 사라진다.
 */
function toValue(node: Node): unknown {
  switch (node.type) {
    case "object": {
      const value: Record<string, unknown> = {};
      for (const property of node.children ?? []) {
        const [key, child] = property.children ?? [];
        if (key !== undefined && child !== undefined) {
          Object.defineProperty(value, String(key.value), {
            value: toValue(child),
            enumerable: true,
            writable: true,
            configurable: true,
          });
        }
      }
      return value;
    }
    case "array":
      return (node.children ?? []).map(toValue);
    default:
      return node.value;
  }
}

function findDuplicateKey(node: Node): Node | null {
  if (node.type === "object") {
    const seen = new Set<unknown>();
    for (const property of node.children ?? []) {
      const key = property.children?.[0];
      if (key !== undefined && seen.has(key.value)) {
        return key;
      }
      seen.add(key?.value);
    }
  }
  for (const child of node.children ?? []) {
    const found = findDuplicateKey(child);
    if (found !== null) {
      return found;
    }
  }
  return null;
}

function spanOf(node: Node): JsonSpan {
  return { offset: node.offset, length: node.length };
}

type SyntaxErrorName = ReturnType<typeof printParseErrorCode>;

const SYNTAX_ERROR_TEXT: Readonly<Record<SyntaxErrorName, string>> = {
  InvalidSymbol: "JSON 에 쓸 수 없는 글자입니다.",
  InvalidNumberFormat: "숫자 형식이 맞지 않습니다.",
  PropertyNameExpected: "키 이름이 와야 합니다. 키는 큰따옴표로 감싸 주세요.",
  ValueExpected: "값이 와야 합니다.",
  ColonExpected: "키 뒤에 콜론(:)이 와야 합니다.",
  CommaExpected: "항목 사이에 쉼표(,)가 와야 합니다.",
  CloseBraceExpected: "객체를 닫는 } 가 와야 합니다.",
  CloseBracketExpected: "배열을 닫는 ] 가 와야 합니다.",
  EndOfFileExpected: "JSON 값 하나가 끝난 뒤에는 아무것도 쓸 수 없습니다.",
  InvalidCommentToken: "JSON 에는 주석을 쓸 수 없습니다.",
  UnexpectedEndOfComment: "JSON 에는 주석을 쓸 수 없습니다.",
  UnexpectedEndOfString: "문자열을 닫는 큰따옴표가 없습니다.",
  UnexpectedEndOfNumber: "숫자가 끝나지 않았습니다.",
  InvalidUnicode: "유니코드 escape(\\u) 형식이 맞지 않습니다.",
  InvalidEscapeCharacter: "문자열 안의 escape 문자가 맞지 않습니다.",
  InvalidCharacter: "문자열 안에 쓸 수 없는 제어 문자가 있습니다.",
  "<unknown ParseErrorCode>": "JSON 문법이 맞지 않습니다.",
};

function describeSyntaxError(text: string, error: ParseError): string {
  const name = printParseErrorCode(error.error);
  // 끝 쉼표는 닫는 괄호 자리에서 "키 · 값이 와야 합니다" 로 보고된다. 사람이 고칠 것은 쉼표다.
  if (
    (name === "PropertyNameExpected" || name === "ValueExpected") &&
    (text[error.offset] === "}" || text[error.offset] === "]") &&
    text.slice(0, error.offset).trimEnd().endsWith(",")
  ) {
    return "마지막 항목 뒤에는 쉼표를 쓸 수 없습니다.";
  }
  return SYNTAX_ERROR_TEXT[name];
}
